/**
 * Общая разметка форм входа и регистрации.
 *
 * Формы отличаются двумя полями и текстами, поэтому дублировать вёрстку,
 * фокус и обработку ошибок в двух файлах смысла нет.
 */

import type { FormEvent, ReactNode } from 'react'

import { strings } from '@/lib/strings'

interface AuthFormProps {
  title: string
  submitLabel: string
  pending: boolean
  error: string | null
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  children: ReactNode
  footer: ReactNode
  /** Блок над формой: кнопки входа через провайдеров (ADR-0008). */
  aside?: ReactNode
}

export function AuthForm({
  title,
  submitLabel,
  pending,
  error,
  onSubmit,
  children,
  footer,
  aside,
}: AuthFormProps) {
  return (
    <section className="mx-auto max-w-sm">
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>

      {aside}

      <form onSubmit={onSubmit} className="mt-6 flex flex-col gap-4" noValidate>
        {children}

        {error !== null && (
          // role=alert: ошибку входа должен услышать и скринридер.
          <p role="alert" className="rounded-lg border border-warning/50 p-3 text-sm text-warning">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={pending}
          className="rounded-lg bg-accent px-4 py-2.5 font-medium text-accent-fg disabled:opacity-60"
        >
          {pending ? strings.auth.pending : submitLabel}
        </button>
      </form>

      <p className="mt-6 text-sm text-muted">{footer}</p>
    </section>
  )
}

interface FieldProps {
  id: string
  label: string
  type: string
  value: string
  autoComplete: string
  required?: boolean
  hint?: string
  onChange: (value: string) => void
}

export function Field({
  id,
  label,
  type,
  value,
  autoComplete,
  required = false,
  hint,
  onChange,
}: FieldProps) {
  const hintId = hint ? `${id}-hint` : undefined
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        required={required}
        autoComplete={autoComplete}
        aria-describedby={hintId}
        onChange={(event) => {
          onChange(event.target.value)
        }}
        className="rounded-lg border border-border bg-surface px-3 py-2"
      />
      {hint && (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      )}
    </div>
  )
}
